<template>
    <rkm-modal
        ref="RkmModal"
        title="Procedimentos"
        extraLarge
    >

        <rkm-area
            title="Adicionar procedimento"
            type="fieldset"
            no-margin-top
        >

            <rkm-form ref="RkmForm">

                <rkm-row v-if="loaded">
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.usuario"
                            :requestQueryStringParams="autocompletesRequestParams.usuario"
                            :searchFields="fields.autocompletes.usuario"
                            :validations="validations.usuario"
                            text-field="cidadaoNome"
                            label="Profissional"
                            placeholder="Digite o nome ou número do conselho profissional"
                            v-model="usuario"
                            uppercase
                        />

                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            label="Código da equipe"
                            :options="equipes"
                            textField="descricao"
                            v-model="equipeId"
                            :validations="validations.equipe"
                            valueField="id"
                            show-default-option
                            :disabled="usuario && usuario.id ? false : true"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            label="Ocupação"
                            :options="ocupacoes"
                            textField="codigoNome"
                            v-model="ocupacaoId"
                            :validations="validations.ocupacao"
                            valueField="id"
                            show-default-option
                            :disabled="equipeId ? false : true"
                        />
                    </rkm-col>

                    <rkm-col class="pb-2 flex-grow-1">
                        <rkm-form-input-autocomplete
                            :repository="repositories.procedimento"
                            :request-query-string-params="autocompletesRequestParams.procedimento.queryString"
                            :search-fields="fields.autocompletes.procedimento"
                            :text-field="p => `${formatProcedimento(p.codigo)} - ${p.nome}`"
                            name="Procedimento"
                            label="Procedimento"
                            v-model="procedimento"
                            :validations="validations.procedimento"

                        >

                            <template v-slot:itemLabel="{ item }">
                                {{ formatProcedimento(item.codigo) }} - {{ item.nome }}
                            </template>

                            <rkm-input-group-append slot="append">
                                <rkm-input-group-text>
                                    <rkm-form-radio-group v-model="searchByApelido">
                                        <rkm-form-radio :value="false">Código / Nome</rkm-form-radio>
                                        <rkm-form-radio :value="true">Apelido</rkm-form-radio>
                                    </rkm-form-radio-group>
                                </rkm-input-group-text>
                            </rkm-input-group-append>
                        </rkm-form-input-autocomplete>
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            color="primary"
                            @click="saveProcedimento()"
                        >
                            Incluir
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="resetForm"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            title="Listagem"
            no-margin-top
        >
            <rkm-table
                v-if="loaded"
                :fields="fields.procedimento"
                :items="model.procedimentos"
            >

                <template v-slot:cell(codigo)="row">
                    {{ row.item.codigo | formatProcedimento }}
                </template>

                <template v-slot:cell(actions)="{ index }">

                    <rkm-button
                        @click="destroy(index)"
                        color="danger"
                        small
                        title="Excluir"
                    >
                        <rkm-icon icon="trash" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>

        <template v-slot:footer="{cancel}">
            <rkm-button @click="cancel">
                Fechar
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>

// Mixins.
import { repository, object, formats, stringCase, data } from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

// Moment.
import moment from 'moment'

// Filters.
import format from '@/filters/formats'

export default {
    computed: {
        validations() {
            return {
                usuario: { required: true },
                procedimento: { required: true },
                ocupacao: { required: true },
            }
        },
        autocompletesRequestParams() {

            return {
                usuario: {
                    with_not_regularized: true,
                    with_atencao_basica_equipe: true,
                },
                procedimento: {
                    queryString: {
                        search_by_apelido: this.searchByApelido,
                        paciente_id: this.model ? this.model.pacienteId : null,
                        cbo: this.ocupacaoId,
                        competencia: this.competencia
                    }
                }
            }
        },
        competencia() {

            if (this.model && this.model.data) {

                return moment(this.model.data).format('YYYYMM')
            }

            return ''
        },
        fields() {

            return {
                procedimento: [
                    {
                        key: "usuario.cidadaoNome",
                        label: "Profissional",
                    },
                    {
                        key: "equipe",
                        label: "Equipe",
                    },
                    {
                        key: "ocupacao",
                        label: "Ocupação",
                    },
                    {
                        key: "codigo",
                        label: "Código",
                        class: "col-min",
                    },
                    {
                        key: "nome",
                        label: "Procedimento",
                    },
                    {
                        key: "actions",
                        label: "Ações",
                        class: "col-min",
                    },
                ],
                autocompletes: {
                    procedimento: this.searchByApelido
                        ? [{ key: 'parametrizacao_procedimento.apelido', searchable: true }]
                        : [{ key: 'nome', searchable: true }, { key: 'codigo', searchable: true }],
                    usuario: [
                        { key: "cadastroCidadao.nome", searchable: true },
                        { key: "cadastroUsuario.registroNumero", searchable: true },
                    ],
                }
            }
        },
    },
    created() {
        this.repositories = {
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            fechamento: this.getConectaSusRepository('ambulatorio.fechamentoConsulta'),
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
        }
    },
    data: () => ({
        id: null,
        loaded: false,
        model: null,
        procedimento: null,
        usuario: null,
        unidade: null,
        infraestrutura: null,
        ocupacaoId: null,
        equipeId: null,
        searchByApelido: false,
        equipes: [],
        ocupacoes: [],
    }),
    filters: {
        ...format,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(item) {

            this.model = item
            this.loaded = true

            this.unidade = item.unidade
            this.infraestrutura = item.infraestrutura
            this.usuario = item.cadastro.usuario

            this.$refs.RkmModal.show()
        },
        getEquipes() {

            const request = {
                usuarioId: this.usuario.id,
                infraestruturaId: this.model.infraestruturaId,
                pacienteId: this.model.pacienteId,
                data: this.model.data,
            }

            this.setLoading()

            this.repositories.fechamento.getEquipes(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.equipes = Object.assign(response.data)

                    if (response.data.length === 1){

                        this.equipeId = response.data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getOcupacoes() {

            this.setLoading()

            const request = {
                usuarioId: this.usuario.id,
                infraestruturaId: this.model.infraestruturaId,
                pacienteId: this.model.pacienteId,
                data: this.model.data,
            }

            this.repositories.fechamento.getOcupacoes(this.camelObjectKeysToSnakeRecursive(request))
                .then(({ data }) => {

                    this.ocupacoes = data

                    if (data.length === 1){

                        this.ocupacaoId = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        saveProcedimento() {

            this.$refs.RkmForm.validate()
                .then(valid => {

                    if (!valid) return

                    const procedimento = this.model.procedimentos
                        .find(procedimento => procedimento.id == this.procedimento.id && procedimento.usuarioId == this.usuario.id)

                    if (this.model.procedimento && this.model.procedimento.id == this.procedimento.id) {

                        this.showAlert(`Este procedimento já está cadastrado como procedimento padrão, portanto não é possível
                            cadastrá-lo novamente.`, "Atenção")
                        return false
                    }

                    if (procedimento) {

                        this.showAlert('O procedimento já foi cadastrado anteriormente para este profissional nesta ficha.', "Atenção")
                        return false
                    }

                    const equipe = this.equipes.length > 0 ? this.equipes.find(e => e.id === this.equipeId).descricao : null
                    const ocupacao = this.ocupacoes.find(e => e.id === this.ocupacaoId).codigoNome

                    const param = {
                        id: this.procedimento.id,
                        nome: this.procedimento.nome,
                        codigo: this.procedimento.codigo,
                        usuario: this.usuario,
                        usuarioId: this.usuario.id,
                        equipeId: this.equipeId,
                        ocupacaoId: this.ocupacaoId,
                        equipe: equipe,
                        ocupacao: ocupacao,
                        origem: 'ambulatorio',
                        quantidade: 1,
                        padrao: false,
                    }

                    this.model.procedimentos.push(param)

                    this.$emit('proc', this.model.procedimentos)

                    this.resetProcedimento()
                })
        },
        resetProcedimento() {

            this.procedimento = null

            this.$refs.RkmForm.resetValidator()
        },
        destroy(index) {

            const destroy = async () => this.model.procedimentos.splice(index, 1)

            this.showDeleteConfirmation('Deseja realmente excluir o procedimento?', destroy)
        },
        resetForm() {

            this.ocupacoes = []
            this.equipes = []

            this.usuario = null
            this.ocupacaoId = null
            this.equipeId = null
            this.procedimento = null

            this.$refs.RkmForm.resetValidator()
        },
    },
    mixins: [ repository, object, formats, stringCase, data ],
    watch: {
        'usuario': function (usuario) {

            if (usuario) {

                this.ocupacoes = []
                this.equipes = []

                this.ocupacaoId = null
                this.equipeId = null
                this.procedimento = null

                this.getEquipes()
                this.getOcupacoes()
            }
        }
    }
}
</script>